import React, { useState, useMemo, useEffect } from 'react';
import styles from '../../../styles/ReportsDashboard.module.css';
import { useReports } from '../../../hooks/useReports';
import DocumentReports from './DocumentReports';
import ListingReports from './AgencyListingReports';
import type { ListingSummary } from '../../../types/ListingTypes';
import { useListingContext } from '../../../contexts/ListingContext';
import { useDashboardContext } from 'contexts/DashboardContext';
import { useManagerContext } from 'contexts/ManagerContext';

interface AgencyReportsProps {
    userDetails: any
}

type ReportTab = 'compliance' | 'listings';


const AgencyReports: React.FC<AgencyReportsProps> = ({ userDetails }) => {
    const [activeTab, setActiveTab] = useState<ReportTab>('compliance');
    const agencyName = userDetails?.agency?.name ?? 'Agency';
    const userName = userDetails
        ? `${userDetails.fullNames ?? ''} ${userDetails.lastName ?? ''}`.trim()
        : '';

    const { agencyStats, categoriesWithStats } = useDashboardContext();
    const generatedAt = useMemo(() => new Date().toLocaleString('en-ZA'), []);

    const [listings, setListings] = useState<ListingSummary[]>([]);
    const [listingsLoading, setListingsLoading] = useState(true);
    const { fetchAgencyListings } = useListingContext();

    const { agencyUsers } = useManagerContext();

    const agentRows = agencyUsers
        .map(user => ({
            id: user.user?.id,
            name: user.user?.displayName,
            email: user.user?.email,
            completionRate: user.stats?.completionRate ?? 0,
        }))
        .sort(
            (a, b) => (b.completionRate ?? 0) - (a.completionRate ?? 0)
        );

    const { downloadPDF, isGenerating } = useReports({
        listings: listings,
        agentRows: agentRows,
        overallStats: agencyStats,
        categoriesWithStats: categoriesWithStats
    });

    useEffect(() => {
        setListingsLoading(true);
        let fetchPromise = fetchAgencyListings(userDetails.agency.agencyId);

        if (!fetchPromise) {
            setListingsLoading(false);
            return;
        }

        fetchPromise
            .then(setListings)
            .catch(() => { })
            .finally(() => setListingsLoading(false));
    }, [
        userDetails?.id,
        userDetails?.agency?.id,
        fetchAgencyListings
    ]);


    return (
        <div className={styles.reports}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Agency Compliance &amp; Listings Report</h1>
                <p className={styles.pageSubtitle}>
                    {agencyName} &nbsp;·&nbsp; Generated {generatedAt}
                </p>
                {userName && (
                    <p className={styles.pageSubtitle}>
                        Prepared for: {userName} ({userDetails?.role})
                    </p>
                )}
            </div>
            {/* ── Tab bar + action buttons ── */}
            <div className={styles.toolbar}>
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'compliance' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('compliance')}
                    >
                        📄 Document Compliance
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'listings' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('listings')}
                    >
                        🏠 Listings
                    </button>
                </div>
                <div className={styles.actionButtons}>
                    <button
                        className={styles.printBtn}
                        onClick={() => downloadPDF({ userDetails })}
                        disabled={isGenerating}
                    >
                        {isGenerating ? '⏳ Generating…' : '⬇ Export PDF'}
                    </button>
                </div>
            </div>
            <div className={styles.reportBody} >
                {/* ── Printable report body ── */}
                {/* ── Document Compliance ── */}
                {activeTab === 'compliance' && (
                    <>
                        <DocumentReports overallStats={agencyStats} categoriesWithStats={categoriesWithStats} managerView={true} />
                    </>
                )}

                {/* ── Listings ── */}
                {activeTab === 'listings' && (
                    <ListingReports userDetails={userDetails} listings={listings} listingsLoading={listingsLoading} />
                )}

            </div>
        </div>
    )
}

export default AgencyReports;